import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Copy, Check, Users, Rss, ExternalLink, X, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/utils";
import { NextGameCard } from "@/components/NextGameCard";
import { useSiteContentCache } from "@/contexts/SiteContentCache";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const TYPE_OPTIONS = [
  { value: "games", label: "Games only" },
  { value: "games_practices", label: "Games & Practices" },
  { value: "all", label: "Everything" },
];

const TYPE_COLORS = {
  game: "bg-[#C8E64E]/25 text-[#0C2E3C] border-[#C8E64E]",
  practice: "bg-white text-[#0C2E3C]/70 border-[#0C2E3C]/15",
  training: "bg-white text-[#0C2E3C]/70 border-[#0C2E3C]/15",
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TeamSchedulesPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState(() => {
    const q = searchParams.get("teams");
    return q ? q.split(",").filter(Boolean) : [];
  });
  const [types, setTypes] = useState(searchParams.get("types") || "games_practices");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [copied, setCopied] = useState(false);
  const [copiedTeam, setCopiedTeam] = useState(null);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);
  const siteContent = useSiteContentCache();

  useEffect(() => {
    const logo = siteContent?.data?.site_logo_url || siteContent?.data?.logo_url;
    if (logo) setLogoUrl(logo);
  }, [siteContent]);

  useEffect(() => {
    axios.get(`${API}/schedule/teams`)
      .then((res) => {
        const list = res.data.teams || [];
        setTeams(list);
        if (slug) {
          const match = list.find((t) => t.slug === slug);
          if (match) setSelectedTeams([match.team]);
        }
      })
      .catch(() => setTeams([]));
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ types });
    if (selectedTeams.length) params.set("teams", selectedTeams.join(","));
    axios.get(`${API}/schedule/events?${params.toString()}`)
      .then((res) => setEvents(res.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [selectedTeams, types]);

  // Keep the URL shareable (skipped on short-link team pages)
  useEffect(() => {
    if (slug) return;
    const next = {};
    if (selectedTeams.length) next.teams = selectedTeams.join(",");
    if (types !== "games_practices") next.types = types;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams, types, slug]);

  const feedUrl = useMemo(() => {
    const params = new URLSearchParams({ types });
    if (selectedTeams.length) params.set("teams", selectedTeams.join(","));
    return `${API}/schedule/feed.ics?${params.toString()}`;
  }, [selectedTeams, types]);

  const webcalUrl = feedUrl.replace(/^https?:/, "webcal:");
  const googleUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feedUrl)}`;

  const toggleTeam = (team) => {
    setSelectedTeams((prev) => prev.includes(team) ? prev.filter((t) => t !== team) : [...prev, team]);
  };

  const copyText = async (text, onDone) => {
    if (await copyToClipboard(text)) {
      toast.success("Link copied — paste it in a text or group chat");
      onDone?.();
    } else {
      toast.error("Could not copy — select the link manually");
    }
  };

  const copyTeamLink = (team) => {
    const url = `${window.location.origin}/t/${team.slug}`;
    copyText(url, () => {
      setCopiedTeam(team.slug);
      setTimeout(() => setCopiedTeam(null), 2500);
    });
  };

  const copyFeed = () => copyText(feedUrl, () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  });

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((evt) => {
      const local = new Date(evt.start_iso);
      const key = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, "0")}-${String(local.getDate()).padStart(2, "0")}`;
      if (!map[key]) map[key] = [];
      map[key].push(evt);
    });
    return map;
  }, [events]);

  const upcoming = useMemo(() => {
    const now = Date.now() - 6 * 3600 * 1000;
    return events.filter((e) => new Date(e.start_iso).getTime() >= now);
  }, [events]);

  const nextGame = useMemo(() => {
    const now = Date.now();
    const evt = upcoming.find((e) => e.event_type === "game" && new Date(e.start_iso).getTime() >= now);
    if (!evt) return null;
    return {
      slug: "next",
      team: evt.team,
      title: evt.title,
      location: evt.location,
      start_iso: evt.start_iso,
    };
  }, [upcoming]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push({ day: null, date: null });
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ day: d, date: `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    return days;
  }, [viewMonth, viewYear]);

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear((y) => y - 1)) : setViewMonth((m) => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear((y) => y + 1)) : setViewMonth((m) => m + 1);

  const headline = slug && selectedTeams.length === 1 ? selectedTeams[0] : "Team Schedules";

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>{`${headline} | Commonwealth FC Schedule`}</title>
        <meta name="description" content="Filter Commonwealth FC team schedules by team and event type, then subscribe to a live calendar feed in Google, Apple, or Outlook Calendar." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/parents" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Parent Hub</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-2" data-testid="team-schedules-title">
          {headline}
        </h1>
        <p className="text-[#0C2E3C]/60 mb-8 max-w-2xl">
          Pick your teams, choose what you want to see, then subscribe once — your calendar stays up to date automatically as the schedule changes.
        </p>

        {nextGame && (
          <div className="mb-8 max-w-2xl" data-testid="next-game-hero">
            <NextGameCard game={nextGame} featured />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Filters + subscribe */}
          <div className="space-y-6">
            <div className="bg-white border border-[#0C2E3C]/10 rounded-sm p-5" data-testid="team-filter-panel">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] flex items-center gap-2">
                  <Users size={16} /> Teams
                </h2>
                {selectedTeams.length > 0 && (
                  <button onClick={() => setSelectedTeams([])} className="text-[11px] font-bold uppercase tracking-wide text-[#0C2E3C]/50 hover:text-[#0C2E3C] flex items-center gap-1" data-testid="clear-teams-btn">
                    <X size={11} /> Clear
                  </button>
                )}
              </div>
              {teams.length === 0 ? (
                <p className="text-sm text-[#0C2E3C]/50">No team schedules published yet.</p>
              ) : (
                <>
                  <p className="text-xs text-[#0C2E3C]/50 mb-3">
                    {selectedTeams.length === 0 ? "All teams included" : `${selectedTeams.length} selected`}
                  </p>
                  <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {teams.map((t) => {
                      const active = selectedTeams.includes(t.team);
                      return (
                        <div
                          key={t.team}
                          className={`flex items-center gap-1 px-2 py-1.5 rounded-sm border transition-colors ${active ? "bg-[#0C2E3C] border-[#0C2E3C]" : "bg-[#F0F4F3] border-transparent hover:border-[#0C2E3C]/20"}`}
                        >
                          <button
                            onClick={() => toggleTeam(t.team)}
                            className="flex-1 flex items-center justify-between gap-2 text-left min-w-0"
                            data-testid={`team-toggle-${t.slug}`}
                          >
                            <span className={`text-sm font-medium truncate ${active ? "text-white" : "text-[#0C2E3C]"}`}>{t.team}</span>
                            <span className={`text-[10px] font-bold ${active ? "text-[#C8E64E]" : "text-[#0C2E3C]/40"}`}>{t.total_events}</span>
                          </button>
                          <button
                            onClick={() => copyTeamLink(t)}
                            title={`Copy shareable link for ${t.team}`}
                            className={`p-1 rounded-sm transition-colors ${active ? "text-[#C8E64E] hover:bg-white/10" : "text-[#0C2E3C]/35 hover:text-[#0C2E3C] hover:bg-white"}`}
                            data-testid={`copy-team-link-${t.slug}`}
                          >
                            {copiedTeam === t.slug ? <Check size={13} /> : <Link2 size={13} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="bg-white border border-[#0C2E3C]/10 rounded-sm p-5">
              <h2 className="text-base md:text-lg font-heading font-bold text-[#0C2E3C] mb-3">Show</h2>
              <div className="space-y-1.5">
                {TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTypes(opt.value)}
                    className={`w-full px-3 py-2 rounded-sm border text-left text-sm font-medium transition-colors ${types === opt.value ? "bg-[#C8E64E] border-[#C8E64E] text-[#0C2E3C]" : "bg-[#F0F4F3] border-transparent text-[#0C2E3C]/70 hover:border-[#0C2E3C]/20"}`}
                    data-testid={`type-toggle-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-[#0C2E3C] rounded-sm p-5" data-testid="subscribe-panel">
              <h2 className="text-base md:text-lg font-heading font-bold text-white mb-1 flex items-center gap-2">
                <Rss size={16} className="text-[#C8E64E]" /> Subscribe
              </h2>
              <p className="text-xs text-[#F0F4F3]/60 mb-4">
                Live feed for your current filters. Add it once — new games and changes appear automatically.
              </p>
              <div className="space-y-2">
                <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid="add-to-google-btn">
                  <Button className="w-full bg-[#C8E64E] text-[#0C2E3C] hover:bg-[#C8E64E]/85 font-bold text-xs uppercase tracking-wider rounded-sm">
                    Add to Google Calendar <ExternalLink size={12} className="ml-1.5" />
                  </Button>
                </a>
                <a href={webcalUrl} data-testid="add-to-apple-btn">
                  <Button variant="outline" className="w-full mt-2 border-[#F0F4F3]/25 bg-transparent text-[#F0F4F3] hover:bg-[#F0F4F3]/10 hover:text-white font-bold text-xs uppercase tracking-wider rounded-sm">
                    Add to Apple / Outlook
                  </Button>
                </a>
                <button
                  onClick={copyFeed}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-sm bg-[#F0F4F3]/10 hover:bg-[#F0F4F3]/15 transition-colors text-left"
                  data-testid="copy-feed-btn"
                >
                  {copied ? <Check size={13} className="text-[#C8E64E] flex-shrink-0" /> : <Copy size={13} className="text-[#C8E64E] flex-shrink-0" />}
                  <span className="text-[10px] text-[#F0F4F3]/70 font-mono truncate">{feedUrl}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#0C2E3C]/60" data-testid="schedule-count">
                {loading ? "Loading…" : `${upcoming.length} upcoming · ${events.length} total`}
              </p>
              <div className="flex gap-1 bg-white border border-[#0C2E3C]/10 rounded-sm p-0.5">
                {["list", "calendar"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors ${view === v ? "bg-[#0C2E3C] text-white" : "text-[#0C2E3C]/50 hover:text-[#0C2E3C]"}`}
                    data-testid={`view-${v}-btn`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" />
              </div>
            ) : events.length === 0 ? (
              <div className="bg-white border border-[#0C2E3C]/10 rounded-sm p-10 text-center" data-testid="schedule-empty">
                <Calendar size={32} className="mx-auto text-[#0C2E3C]/20 mb-2" />
                <p className="text-sm text-[#0C2E3C]/50">No events match these filters</p>
              </div>
            ) : view === "list" ? (
              <div className="space-y-2" data-testid="schedule-list">
                {(upcoming.length ? upcoming : events).map((evt) => <ScheduleRow key={evt.event_id} event={evt} />)}
              </div>
            ) : (
              <div className="bg-white rounded-sm border border-[#0C2E3C]/10 overflow-hidden" data-testid="schedule-calendar">
                <div className="flex items-center justify-between px-5 py-4 bg-[#0C2E3C]">
                  <button onClick={prevMonth} className="text-[#C8E64E] hover:text-white transition-colors" data-testid="schedule-prev-month"><ChevronLeft size={20} /></button>
                  <h3 className="text-white font-heading font-bold text-lg tracking-wider">{MONTH_NAMES[viewMonth]} {viewYear}</h3>
                  <button onClick={nextMonth} className="text-[#C8E64E] hover:text-white transition-colors" data-testid="schedule-next-month"><ChevronRight size={20} /></button>
                </div>
                <div className="grid grid-cols-7 border-b border-[#0C2E3C]/10">
                  {DAY_NAMES.map((d) => (
                    <div key={d} className="py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#0C2E3C]/50">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {calendarDays.map((cell, i) => {
                    const dayEvents = cell.date ? (eventsByDate[cell.date] || []) : [];
                    const isToday = cell.date === new Date().toLocaleDateString("en-CA");
                    return (
                      <div key={i} className={`min-h-[80px] p-1.5 border-b border-r border-[#0C2E3C]/5 ${!cell.day ? "bg-[#F0F4F3]/50" : isToday ? "bg-[#C8E64E]/10" : ""}`} data-testid={cell.date ? `schedule-day-${cell.date}` : undefined}>
                        {cell.day && (
                          <>
                            <span className="text-xs font-bold text-[#0C2E3C]/70">{cell.day}</span>
                            <div className="mt-0.5 space-y-0.5">
                              {dayEvents.slice(0, 3).map((evt) => (
                                <div key={evt.event_id} className={`text-[9px] font-medium truncate px-1 py-0.5 rounded border ${TYPE_COLORS[evt.event_type] || TYPE_COLORS.practice}`} title={evt.title}>
                                  {evt.title}
                                </div>
                              ))}
                              {dayEvents.length > 3 && <span className="text-[9px] text-[#0C2E3C]/50 px-1">+{dayEvents.length - 3}</span>}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

const ScheduleRow = ({ event }) => {
  const start = new Date(event.start_iso);
  const isGame = event.event_type === "game";
  return (
    <div className="bg-white border border-[#0C2E3C]/10 rounded-sm p-3 flex items-center gap-4 hover:border-[#0C2E3C]/25 transition-colors" data-testid={`schedule-row-${event.event_id}`}>
      <div className={`w-12 flex-shrink-0 flex flex-col items-center justify-center py-1 rounded-sm ${isGame ? "bg-[#0C2E3C]" : "bg-[#F0F4F3]"}`}>
        <span className={`text-[9px] font-bold uppercase ${isGame ? "text-[#C8E64E]" : "text-[#0C2E3C]/50"}`}>
          {start.toLocaleDateString("en-US", { month: "short" })}
        </span>
        <span className={`text-lg font-bold leading-none ${isGame ? "text-white" : "text-[#0C2E3C]"}`}>{start.getDate()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${TYPE_COLORS[event.event_type] || TYPE_COLORS.practice}`}>
            {event.event_type}
          </span>
          {event.team && <span className="text-[10px] text-[#0C2E3C]/50 font-medium truncate">{event.team}</span>}
        </div>
        <h4 className="font-heading font-bold text-[#0C2E3C] text-sm leading-tight truncate">{event.title}</h4>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-[#0C2E3C]/50">
          <span className="flex items-center gap-1 flex-shrink-0"><Clock size={11} /> {start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
          {event.location && <span className="flex items-center gap-1 truncate"><MapPin size={11} /> {event.location}</span>}
        </div>
      </div>
    </div>
  );
};

export default TeamSchedulesPage;
